// audit-safety-integrated
import { readJson, safeErrorMessage } from '../utils/auditSafety';
import { useState, useEffect, useMemo } from "react";
import { API_BASE_URL } from '../services/api';
import { FileText } from "lucide-react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const subCols = [
  { key: "grocery_coop_multi",         label: "Coop >1 Brand",     type: "multi",  group: "grocery" },
  { key: "grocery_coop_single",        label: "Coop 1 Brand",      type: "single", group: "grocery" },
  { key: "grocery_nonjpc_multi",       label: "Non-JPC >1 Brand",  type: "multi",  group: "grocery" },
  { key: "grocery_nonjpc_single",      label: "Non-JPC 1 Brand",   type: "single", group: "grocery" },
  { key: "housekeeping_nonjpc_multi",  label: "Non-JPC >1 Brand",  type: "multi",  group: "hk" },
  { key: "housekeeping_nonjpc_single", label: "Non-JPC 1 Brand",   type: "single", group: "hk" },
];

export default function StoreTrendReport() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [sortKey, setSortKey] = useState("store");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch]   = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {

      const res = await fetch(`${API_BASE_URL}/item/store-trend`, {
        method: 'GET',
        headers: {

          'Content-Type': 'application/json',
        },
      });
      const json = await readJson(res);
      if (!res.ok || json.error) throw new Error(json.message || `HTTP ${res.status}`);
      setData(json.data || []);
    } catch (err) {
      setError(safeErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const sorted = useMemo(() => {
    let rows = [...data];
    if (search.trim()) rows = rows.filter(r => r.store.toLowerCase().includes(search.toLowerCase()));
    rows.sort((a, b) => {
      const av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [data, sortKey, sortDir, search]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => (
    <span className={`ml-1 text-xs ${sortKey === col ? "text-blue-600" : "text-gray-400"}`}>
      {sortKey === col ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}
    </span>
  );

  const ValueBadge = ({ value, type }) => {
    if (value === 0) return <span className="text-gray-400 text-sm">—</span>;
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
        type === "multi" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"
      }`}>
        {value}
      </span>
    );
  };

  const handleExport = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Store Trends");

    const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

    ws.mergeCells("A1:G1");
    ws.getCell("A1").value = "Number of Items with Brands in the Store";
    ws.getCell("A1").font = { bold: true, size: 14 };
    ws.getCell("A1").alignment = { horizontal: "center" };

    ws.mergeCells("A2:G2");
    ws.getCell("A2").value = `Generated: ${today}`;
    ws.getCell("A2").font = { size: 10, color: { argb: "FF888888" } };
    ws.getCell("A2").alignment = { horizontal: "center" };

    ws.addRow([]);

    const groupRow = ws.addRow(["Store", "Grocery", "", "", "", "Housekeeping", ""]);
    ws.mergeCells("B4:E4");
    ws.mergeCells("F4:G4");
    groupRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD6E4F7" } };
    });

    const subRow = ws.addRow([
      "Store",
      "Coop >1 Brand", "Coop 1 Brand", "Non-JPC >1 Brand", "Non-JPC 1 Brand",
      "Non-JPC >1 Brand", "Non-JPC 1 Brand",
    ]);
    subRow.eachCell((cell) => {
      cell.font = { bold: true, size: 10 };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F4FF" } };
    });

    sorted.forEach((row) => {
      const dataRow = ws.addRow([
        row.store,
        row.grocery_coop_multi,
        row.grocery_coop_single,
        row.grocery_nonjpc_multi,
        row.grocery_nonjpc_single,
        row.housekeeping_nonjpc_multi,
        row.housekeeping_nonjpc_single,
      ]);
      dataRow.eachCell((cell, colNumber) => {
        cell.alignment = { horizontal: colNumber === 1 ? "left" : "center", vertical: "middle" };
      });
    });

    ws.columns = [
      { width: 26 },
      { width: 14 }, { width: 14 }, { width: 16 }, { width: 16 },
      { width: 16 }, { width: 16 },
    ];

    const buffer = await wb.xlsx.writeBuffer();
    const dateStr = new Date().toISOString().slice(0, 10);
    saveAs(new Blob([buffer]), `Store_Brand_Trends_${dateStr}.xlsx`);
  };

  return (
    <div className="max-w-7xl mx-auto p-2 bg-gray-50 rounded-lg">
      <div className="bg-white shadow rounded-lg p-6 h-full flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-gray-800">Number of Items with Brands in the Store</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-sm"
            >
              <FileText className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 border border-gray-300 text-gray-600 px-3 py-2 rounded hover:bg-gray-100 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-5">
          <div className="relative w-48">
            <input
              type="text"
              placeholder="Search store..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 w-full rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M15 11a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-2 text-gray-600 text-sm">Loading store trends...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600 font-medium">Failed to load data</p>
            <p className="text-gray-500 text-sm mt-1">{error}</p>
          </div>
        ) : (
          <div className="flex-grow overflow-auto">
            <table className="w-full table-auto border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th rowSpan={2} onClick={() => handleSort("store")}
                    className="border border-gray-300 px-4 py-2 text-left align-middle cursor-pointer whitespace-nowrap">
                    Store <SortIcon col="store" />
                  </th>
                  <th colSpan={4} className="border border-gray-300 px-4 py-2 text-center text-blue-700 font-semibold">
                    🛒 Grocery
                  </th>
                  <th colSpan={2} className="border border-gray-300 px-4 py-2 text-center text-purple-700 font-semibold">
                    🧹 Housekeeping
                  </th>
                </tr>
                <tr className="bg-gray-50">
                  {subCols.map(col => (
                    <th key={col.key} onClick={() => handleSort(col.key)}
                      className={`border border-gray-300 px-3 py-2 text-center cursor-pointer whitespace-nowrap font-medium ${
                        col.type === "multi" ? "text-red-600" : "text-yellow-600"
                      }`}>
                      {col.label} <SortIcon col={col.key} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                      No stores found.
                    </td>
                  </tr>
                ) : sorted.map((row, idx) => (
                  <tr key={row.store}
                    className={`transition-colors duration-150 hover:bg-blue-50 ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}>
                    <td className="border border-gray-300 px-4 py-2 font-medium whitespace-nowrap">
                      {row.store}
                    </td>
                    {subCols.map(col => (
                      <td key={col.key} className="border border-gray-300 px-3 py-2 text-center">
                        <ValueBadge value={row[col.key]} type={col.type} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-right text-xs text-gray-400 mt-2">
              Showing {sorted.length} of {data.length} stores
            </div>
          </div>
        )}
      </div>
    </div>
  );
}